import { FormikProps, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import deepEqual from 'deep-equal';
import {
  setEditCreativeRequest,
  setResponseResultDefault,
} from 'domains/creative/reduser';
import { useEffect } from 'react';
import { useUserTheme } from 'domains/user/model/selectors';
import { useValidateSchemeCreatives } from '../../../../validate/validate';
import { validate } from '../validateOther';
import { CreativeDetail, TCreativeVideoVast } from '../../../../../types';
import { useDispatchApp } from '../../../../../../../redux/rootSelectors';
import { useCreativeSize } from '../../../../../model/hooks/useCreativeSize';
import { FetchedDataErrors } from '../../../../../../../types/general';
import { useCreativeResponseInfo } from '../../../../../model/selectors';
import { useValidateVendorLInk } from '../../../../../model/hooks/useValidateVendorLInk';

type TParam = {
  xxhash: CreativeDetail['xxhash'];
  data: TCreativeVideoVast;
  size: CreativeDetail['size'];
  erid: string;
  setEdit: (param: boolean) => void;
  edit: boolean;
};

type FormikEditVideo = {
  size: string;
  erid?: string;
  width?: number;
  height?: number;
  data: TCreativeVideoVast;
};

type TFUseFormVideo = (p: TParam) => {
  formik: FormikProps<
    FormikEditVideo & {
      additional_settings_switch: 'ALL' | 'QUARTILES' | 'SHOW';
    }
  >;
  handlerFormikReset: () => void;
  handlerFormikSubmit: () => void;
  isLoading: boolean;
  sizeOption: ReturnType<typeof useCreativeSize>['sizeOption'];
  defaultSizeOption: ReturnType<typeof useCreativeSize>['defaultSizeOption'];
  checkSizes: ReturnType<typeof useCreativeSize>['checkSizes'];
  LTU: number;
  error: FetchedDataErrors;
  helper: ReturnType<typeof useValidateVendorLInk>['helper'];
  helperError: ReturnType<typeof useValidateVendorLInk>['helperError'];
};

export const useFormVideo: TFUseFormVideo = ({
  xxhash,
  size,
  erid,
  data: { audit, duration, skip_duration, vast_url, description },
  setEdit,
  edit,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatchApp();
  const { error, isLoading, LTU } = useCreativeResponseInfo();
  const { dataCreativeVideoInline } = useValidateSchemeCreatives();
  const { getValueFromSettingForKey } = useUserTheme();

  const { defaultSizeOption, checkSizes, sizeOption } =
    useCreativeSize('VIDEO_VAST');

  const formik = useFormik<
    FormikEditVideo & {
      additional_settings_switch: 'ALL' | 'QUARTILES' | 'SHOW';
    }
  >({
    enableReinitialize: true,
    validationSchema: dataCreativeVideoInline,
    validate,
    initialValues: {
      size,
      erid,
      additional_settings_switch: 'SHOW',
      data: {
        skip_duration,
        duration,
        audit,
        vast_url,
        description,
      },
      width: 1,
      height: 1,
    },
    validateOnChange: true,
    validateOnMount: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      const formValueData: FormikEditVideo = {
        data: values.data,
        erid: values.erid,
        size: '',
      };
      if (values.size === '1x1') {
        formValueData.size = `${values.width}x${values.height}`;
      } else if (sizeOption.some((v) => v.value === values.size)) {
        formValueData.size = values.size;
      } else if (typeof values.size === 'string' && values.size !== '') {
        const sizeData = formik.values.size.split('x');
        if (values.width !== Number(sizeData[0]) || values.height !== Number(sizeData[1])) {
          formValueData.size = `${values.width}x${values.height}`;
        } else {
          formValueData.size = values.size;
        }
      } else {
        formValueData.size = values.size || '1x1';
      }

      if (!deepEqual({}, values)) {
        dispatch(
          setEditCreativeRequest({
            data: { xxhash, type: 'VIDEO_VAST', ...formValueData },
            visual: {
              setter: setEdit,
              translation:
                getValueFromSettingForKey('ShowBroadcastStatus') !== null &&
                !getValueFromSettingForKey('ShowBroadcastStatus'),
              value: false,
              alertText: `${t('creative.type.type')}${t(
                'creative.type.video_vast',
              )}`,
            },
          }),
        );
      }
    },
  });

  useEffect(() => {
    const defSize = sizeOption.filter((v) => v.value === formik.values.size);
    if (defSize.length === 0) {
      if (formik.values.size) {
        const sizeNew = formik.values.size.split('x');
        formik.setFieldValue('width', sizeNew[0]);
        formik.setFieldValue('height', sizeNew[1]);
      }
    }
    formik.validateForm();
  }, [edit]);

  /** Функция сбрасывает состояние формы */
  const handlerFormikReset = () => {
    if (error.isError) {
      dispatch(setResponseResultDefault());
    }
    setEdit(false);
    formik.handleReset({
      skip_duration,
      duration,
      audit,
      vast_url,
      size,
    });
  };

  /** Функция отправляет форму */
  const handlerFormikSubmit = () => {
    formik.handleSubmit();
  };

  const { helper, helperError } = useValidateVendorLInk({
    formik: {
      show: formik.values.data.audit.show,
      show2: formik.values.data.audit.show2,
    },
  });

  useEffect(() => {
    formik.validateForm();
  }, [formik.values.width, formik.values.height, formik.values.data.vast_url])

  return {
    formik,
    handlerFormikReset,
    handlerFormikSubmit,
    isLoading,
    sizeOption,
    defaultSizeOption,
    checkSizes,
    LTU,
    error,
    helper,
    helperError,
  };
};
